/**
 * Guided sports no-objection certificate UI: maps backend ``/chat/command`` rows to chat
 * (location/sports/comments, generate confirm, mail preview). Flow state and MediKaLLink
 * calls live in the backend (``sports_noc_flow_intent``).
 */

import {
  getSportsCertificateCommentsFieldId,
} from '../api/apiConfig'
import type { VoiceNavSessionActivePatientPayload } from '../api/apiConfig'
import type { PatientContext } from '../types/widget'
import type {
  ChatMessage,
  SportsCertificateFieldOption,
  SportsNocCertificatePayload,
  SportsNocLocationOption,
  SportsNocSportOption,
} from '../types/chat'
import { parseOrdinalChoice } from './patientOptionOrdinal'
import { chatContentString, chatContentTrim } from './chatContent'

export const SPORTS_NOC_INTENT = 'generate_no_objection_certificate_for_sports'

function foldAsciiLower(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
}

/**
 * True when the utterance should start the sports NOC pipeline on the backend
 * (mirrors ``should_route_to_sports_noc_certificate`` in Python).
 */
export function extractSportMentionsFromSportsNocCommand(text: string): string[] {
  const t = (text || '').trim()
  if (!t) return []
  const patterns = [
    /\bcertificat\s+(?:médical|medical)\s+de\s+(.+?)\s+pour\s+/i,
    /\bcertificat\s+d['']aptitude\s+au\s+(.+?)\s+pour\s+/i,
    /\b(?:medical|fitness)\s+certificate\s+for\s+(.+?)\s+for\s+/i,
  ]
  const out: string[] = []
  for (const pat of patterns) {
    const m = t.match(pat)
    if (!m) continue
    const cand = m[1].trim().replace(/^[,.\s]+|[,.\s]+$/g, '')
    if (cand && !isSportsScopeTail(cand)) out.push(cand)
  }
  return out
}

function isSportsScopeTail(s: string): boolean {
  const t = foldAsciiLower((s || '').trim())
  if (!t) return true
  return /^(?:le\s+)?sports?|sport|physical\s+activity|activite\s+physique|athletic(s)?|le\s+sport|la\s+pratique\s+sportive|activité\s+physique|la\s+pratique\s+du\s+sport|pratique\s+du\s+sport|pratique\s+sportive$/i.test(
    t,
  )
}

function hasSportCertificateFamilyPhrase(low: string): boolean {
  return /certificat\s+de\s+sport|sport\s+certificate|fitness\s+certificate|certificat\s+d['']aptitude|certificat\s+(?:médical|medical)\s+de|medical\s+certificate\s+for/i.test(
    low,
  )
}

export function utteranceLooksLikeSportsNocStart(text: string): boolean {
  const raw = (text || '').trim()
  if (raw.length < 12 || raw.length > 500) return false
  const low = foldAsciiLower(raw)

  const hasSportContext =
    /\b(sports?|athletic|athlétique|athletique|physical\s+activity|activité\s+physique|activite\s+physique|pratique\s+du\s+sport|pratique\s+sportive)\b/i.test(
      low,
    )
  const hasCertWord = /\b(certificate|certificat|attestation)\b/i.test(low)
  const hasNocPhrase =
    /\b(no|non)[-\s]+objection\b|\b(no|non)[-\s]+contraindic\w*\b|\bnoc\b|sans\s+objection|sans\s+contre[-\s]*indication|non[-\s]*contre[-\s]*indication|absence\s+de\s+contre[-\s]*indication|certificat\s+de\s+non[\s-]objection|certificat\s+de\s+non[-\s]*contre[-\s]*indication|certificat\s+sans\s+objection/i.test(
      low,
    )
  const hasGenerationCue =
    /\b(generate|generer|create|issue|draft|prepare|produce|write|need|want|get|make|faire|fais|fait|rédiger|rediger|preparer|préparer|émettre|emettre|obtenir|établis|etablis|établir|etablir|adresse|address)\b/i.test(
      low,
    )
  const hasSportCertFamily = hasSportCertificateFamilyPhrase(low)
  const hasNamedSport = extractSportMentionsFromSportsNocCommand(raw).length > 0

  const namedTail = extractSportsNocPatientNameTail(raw)
  if (namedTail) {
    return (
      (hasNocPhrase || hasSportCertFamily || hasNamedSport) &&
      (hasCertWord || hasGenerationCue || hasSportCertFamily)
    )
  }
  if (hasNamedSport && hasCertWord) {
    return hasGenerationCue || hasNocPhrase || hasSportCertFamily
  }
  if (!(hasSportContext || hasSportCertFamily) || !(hasNocPhrase || hasSportCertFamily)) {
    return false
  }
  return hasCertWord || hasGenerationCue
}

/** Rough name tail after ``for`` / ``pour`` / « adresse à » (sports scope tails return empty). */
function extractSportsNocPatientNameTail(raw: string): string {
  const t = raw.trim()
  const adresseMatch = t.match(/\badresse\s+(?:à|a)\s+(.+?)\s+un\s+certificat\b/i)
  if (adresseMatch) {
    const tail = adresseMatch[1].trim()
    if (tail.length >= 2 && tail.length <= 80) return tail
  }
  const issueMatch = t.match(/\bissue\s+(.+?)\s+a\s+certificate\b/i)
  if (issueMatch) {
    const tail = issueMatch[1].trim()
    if (tail.length >= 2 && tail.length <= 80) return tail
  }
  const pairedPatterns = [
    /\bcertificat\s+(?:médical|medical)\s+de\s+.+?\s+pour\s+(.+)$/i,
    /\bcertificat\s+d['']aptitude\s+au\s+.+?\s+pour\s+(.+)$/i,
    /\b(?:medical|fitness)\s+certificate\s+for\s+.+?\s+for\s+(.+)$/i,
  ]
  for (const pat of pairedPatterns) {
    const m = t.match(pat)
    if (m) {
      const tail = m[1].trim()
      if (tail.length >= 2 && tail.length <= 80) return tail
    }
  }
  const forMatch = t.match(/\bfor\s+(.+)$/i)
  if (forMatch) {
    const tail = forMatch[1].trim()
    if (
      /^(?:le\s+)?sports?|sport|physical\s+activity|activite\s+physique|athletic(s)?$/i.test(
        tail,
      )
    ) {
      return ''
    }
    if (tail.length >= 2 && tail.length <= 80) return tail
  }
  const pourMatch = t.match(/\bpour\s+(.+)$/i)
  if (pourMatch) {
    const tail = pourMatch[1].trim()
    if (/^(?:le\s+)?sport|activité\s+physique|pratique\s+du\s+sport/i.test(tail)) return ''
    if (tail.length >= 2 && tail.length <= 80) return tail
  }
  return ''
}

/** Use VoicNav ``/chat/command`` (not host ``sendMessage``) for sports NOC start and follow-ups. */
export function shouldUseHostedChatForSportsNoc(
  text: string,
  messages: readonly ChatMessage[],
  backendSportsNocActive?: boolean,
): boolean {
  return (
    backendSportsNocActive === true ||
    isSportsNocGuidedFlowInProgress(messages) ||
    utteranceLooksLikeSportsNocStart(text)
  )
}

function firstNonEmptyString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    const s = typeof v === 'string' ? v.trim() : v != null ? String(v).trim() : ''
    if (s) return s
  }
  return undefined
}

/**
 * Coerce host-supplied patient context (camelCase, snake_case, or alternate keys)
 * into ``PatientContext``.
 */
export function normalizePatientContext(
  raw: PatientContext | Record<string, unknown> | undefined | null,
): PatientContext | undefined {
  if (raw == null || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const patientId = firstNonEmptyString(
    o.patientId,
    o.patient_id,
    o.patientID,
    o.id,
    o.patientUuid,
    o.patient_uuid,
    o.chartPatientId,
    o.chart_patient_id,
    o.activePatientId,
    o.active_patient_id,
    o.selectedPatientId,
    o.selected_patient_id,
  )
  const name = firstNonEmptyString(
    o.name,
    o.patient_name,
    o.displayName,
    o.fullName,
    o.patientName,
    o.currentPatientName,
    o.current_patient_name,
    o.activePatientName,
    o.active_patient_name,
    o.chartPatientName,
    o.chart_patient_name,
    o.selectedPatientName,
    o.selected_patient_name,
    o.patientLabel,
    o.patient_label,
  )
  const mrn = firstNonEmptyString(o.mrn, o.MRN, o.medical_record_number, o.medicalRecordNumber)
  if (!patientId && !name && !mrn) return undefined
  return { patientId, name, mrn }
}

/** Stable string for effect deps — avoids re-hydrating chat when the host passes a new object reference. */
export function patientContextStableKey(
  raw: PatientContext | Record<string, unknown> | undefined | null,
): string {
  const n = normalizePatientContext(raw)
  if (!n) return ''
  return `${n.patientId ?? ''}|${n.name ?? ''}|${n.mrn ?? ''}`
}

/** MediKaLLink dossier URL built in ``mapAssistantApiRows`` — extract dossier patient id. */
export function patientIdFromOpenPatientDossierUrl(url: string): string | undefined {
  const u = (url || '').trim()
  if (!u) return undefined
  try {
    const parsed = new URL(u)
    const m = parsed.pathname.match(/\/patient\/dossier\/([^/]+)\/?$/)
    if (!m?.[1]) return undefined
    const id = decodeURIComponent(m[1]).trim()
    return id || undefined
  } catch {
    return undefined
  }
}

/**
 * Copilot patient context for intents and flows (doctor-facing).
 *
 * Uses Redis session active chart and recent assistant rows only. Embed
 * ``config.patientContext`` from the host is ignored so placeholder names (e.g. demo
 * patients) never appear in the UI or flow resolution.
 */
export function resolveSportsNocPatientContext(
  _configCtx: PatientContext | Record<string, unknown> | undefined,
  messages: readonly ChatMessage[],
  sessionActive?: VoiceNavSessionActivePatientPayload | null,
): PatientContext | undefined {
  const ap = sessionActive ?? null
  const sessionPid = (ap?.patient_id || '').trim()
  const sessionName = (ap?.patient_name || '').trim()
  if (sessionPid || sessionName) {
    const fromSession = normalizePatientContext({
      patientId: sessionPid || undefined,
      name: sessionName || undefined,
    })
    if (hasActivePatient(fromSession)) return fromSession
  }

  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant') continue

    const hintId = (m.activePatientIdHint || '').trim()
    const hintName = (m.activePatientNameHint || '').trim()
    if (hintId || hintName) {
      return normalizePatientContext({
        patientId: hintId || sessionPid || undefined,
        name: hintName || sessionName || undefined,
      })
    }

    const sm = m.secretaryMessage
    const sid = sm?.patient_id?.trim()
    if (sid) {
      return normalizePatientContext({
        patientId: sid,
        name: sm.patient_name?.trim() || '',
      })
    }

    const pm = m.prescriptionMeta
    const rid = pm?.resolved_patient_id?.trim()
    if (rid) {
      return normalizePatientContext({
        patientId: rid,
        name: pm.mentioned_patient_name?.trim() || '',
      })
    }

    const dossier = m.openPatientDossierUrl
    if (dossier) {
      const pid = patientIdFromOpenPatientDossierUrl(dossier)
      if (pid) return { patientId: pid, name: '' }
    }
  }

  return undefined
}

/** True when host/widget has an identifiable active patient (id, name, or MRN). */
export function hasActivePatient(ctx: PatientContext | Record<string, unknown> | undefined): boolean {
  const n = normalizePatientContext(ctx)
  if (!n) return false
  return Boolean(
    (n.patientId ?? '').trim() || (n.name ?? '').trim() || (n.mrn ?? '').trim(),
  )
}

export function patientFromContext(ctx: PatientContext | Record<string, unknown> | undefined): {
  id: string
  name: string
} {
  const n = normalizePatientContext(ctx) ?? {}
  return {
    id: (n.patientId ?? '').trim(),
    name: (n.name ?? '').trim(),
  }
}

const UUID_RE = /^[0-9a-fA-F-]{36}$/

/** Sports certificate POST requires a patient UUID in context. */
export function hasSportsCertificatePatientUuid(
  ctx: PatientContext | Record<string, unknown> | undefined,
): boolean {
  return UUID_RE.test(patientFromContext(ctx).id)
}

const NO_COMMENTS_RE =
  /^\s*(no|none|nil|n\/a|nope|nothing|non|sans\s+commentaire|aucun(?:e)?\s+commentaire)\b/i

/** Turn selected sport labels into composer text (field_name values). */
export function formatSportsForComposer(sports: string[]): string {
  if (!sports?.length) return ''
  return sports.map((s) => (s || '').trim()).filter(Boolean).join(', ')
}

/** Prefill the composer when editing comments only (empty string if none). */
export function formatCommentsForComposer(comments: string | null | undefined): string {
  if (comments == null || comments === '') return ''
  return String(comments)
}

export function parseSportsInput(text: string): string[] {
  const t = (text || '').trim()
  if (!t) return []
  const parts = t
    .split(/\s*(?:,|;|\/|\n|\u2022|·|•)\s*|\s+\band\b\s+|\s+&\s+/gi)
    .map((p) => p.trim())
    .filter(Boolean)
  const out: string[] = []
  const seen = new Set<string>()
  for (const p of parts) {
    const n = normalizeSport(p)
    if (n.length < 2) continue
    if (seen.has(n)) continue
    seen.add(n)
    out.push(n)
  }
  return out
}

export type SportFieldPick = { fieldId: number; fieldName: string }
export type LocationPick = { locationId: number; locationName: string }

function asciiFold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/**
 * While choosing location, sports, or comments (before the summary Yes/No), the user can
 * abandon the whole certificate flow — e.g. ``cancel it``, ``stop``, ``annuler``.
 */
export function parseSportsNocMidFlowCancel(text: string): boolean {
  const t = asciiFold((text || '').trim())
    .replace(/[.!?,;:]+$/g, '')
    .trim()
  if (!t) return false
  if (
    /^(?:(?:please|i\s+want\s+to|i\s+need\s+to)\s+)?(?:cancel|stop|abort)(?:\s+(?:it|this|that|now))?\b/.test(
      t,
    )
  ) {
    return true
  }
  if (/^cancel\s+the\s+(task|certificate|request)\b/.test(t)) return true
  if (/^(never\s+mind|forget\s+it|give\s+up)\b/.test(t)) return true
  if (/^(annuler|annulez)(?:\s+(?:tout|cela|ca|ça))?\b/.test(t)) return true
  if (/^(non|no)[,\s]+(annuler|annulez|cancel)\b/.test(t)) return true
  if (/^(laissez\s+tomber|laisse\s+tomber)\b/.test(t)) return true
  return false
}

export type ParsedComments =
  | { kind: 'none' }
  | { kind: 'text'; value: string }
  | { kind: 'unclear' }
  | { kind: 'flow_cancel' }

export function parseCommentsInput(text: string): ParsedComments {
  const t = (text || '').trim()
  if (!t) return { kind: 'unclear' }
  if (parseSportsNocMidFlowCancel(t)) return { kind: 'flow_cancel' }
  if (NO_COMMENTS_RE.test(t)) return { kind: 'none' }
  return { kind: 'text', value: t }
}

/** Strip common French articles/prefixes for looser name matching. */
function stripArticlePrefix(s: string): string {
  return s
    .replace(/^(l'|l’|d'|d’|de la |de l'|de l’|du |des |de |le |la |les |un |une )/i, '')
    .trim()
}

function normNameKey(s: string): string {
  return asciiFold(stripArticlePrefix(s)).replace(/\s+/g, ' ').trim()
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Levenshtein distance (small strings only — location words). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  const m = a.length
  const n = b.length
  const row = new Array<number>(n + 1)
  for (let j = 0; j <= n; j++) row[j] = j
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = row[j]
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost)
      prev = tmp
    }
  }
  return row[n]
}

/** Plural / spoken variants so ``hospitals`` ≈ ``hospital`` ≈ ``hopital``; ``hopitaux`` → ``hopital``. */
function expandWordMatchVariants(w: string): string[] {
  const t = (w || '').trim()
  if (!t) return []
  const out = new Set<string>([t])
  if (t.length > 3 && t.endsWith('ies')) {
    out.add(t.slice(0, -3) + 'y')
  }
  if (t.length > 3 && t.endsWith('es')) {
    out.add(t.slice(0, -2))
    out.add(t.slice(0, -1))
  }
  if (t.length > 1 && t.endsWith('s') && !t.endsWith('ss')) {
    out.add(t.slice(0, -1))
  }
  // French plural in -aux → singular -al (hopitaux → hopital)
  if (t.length >= 5 && t.endsWith('aux')) {
    out.add(t.slice(0, -3) + 'al')
  }
  return [...out].filter(Boolean)
}

function maxTokenEditDist(lenA: number, lenB: number): number {
  const m = Math.min(lenA, lenB)
  if (m <= 3) return 1
  if (m <= 7) return 2
  return 3
}

/** Rough consonant frame for light “phonetic” tolerance (hospital / hopital). */
function consonantFrame(s: string): string {
  return s.replace(/[aeiouy]+/g, '')
}

function wordsRoughlyMatch(a: string, b: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  const maxD = maxTokenEditDist(a.length, b.length)
  if (levenshtein(a, b) <= maxD) return true
  if (a.length >= 4 && b.length >= 4) {
    const fa = consonantFrame(a)
    const fb = consonantFrame(b)
    if (fa.length >= 3 && fb.length >= 3) {
      if (fa === fb) return true
      if (levenshtein(fa, fb) <= 1) return true
    }
  }
  return false
}

/**
 * True if normalized token ``p`` matches ``normHaystack`` (substring, whole-word digit,
 * or fuzzy / plural / phonetic word match vs. location words).
 */
function locationSignificantTokenMatches(normHaystack: string, p: string): boolean {
  if (!normHaystack || !p) return false
  if (/^\d+$/.test(p)) {
    return new RegExp(`\\b${escapeRegExp(p)}\\b`).test(normHaystack)
  }
  if (normHaystack.includes(p)) return true
  const locWords = normHaystack.split(/\s+/).filter(Boolean)
  const pVariants = expandWordMatchVariants(p)
  for (const lw of locWords) {
    const locVariants = expandWordMatchVariants(lw)
    for (const pv of pVariants) {
      for (const lv of locVariants) {
        if (!pv || !lv) continue
        if (lv === pv || lv.includes(pv) || pv.includes(lv)) return true
        if (wordsRoughlyMatch(pv, lv)) return true
      }
    }
  }
  return false
}

/**
 * Tokenize user input: commas, "and" / "et", ampersand, semicolons, newlines.
 */
function splitSelectionTokens(text: string): string[] {
  const t = (text || '')
    .replace(/\bet\b/gi, ',')
    .replace(/\band\b/gi, ',')
    .replace(/\bou\b/gi, ',')
    .split(/[,;\n\u2022·•]+/)
    .map((x) => x.trim())
    .filter(Boolean)
  return t
}

/** Collapse inner spaces and trim; strip trailing punctuation (common STT noise). */
function compactOrdinalCandidate(raw: string): string {
  return (raw || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[.,!?;:]+$/g, '')
}

/**
 * Parse a 1-based index from one selection token (numbered sports list).
 * Plain digits, English ordinals (1st, 2nd, 3rd, 4th, …), and French
 * (1er, 1re, 1ère, 2e, 4e, 4ème, 11e, …).
 */
function parseOneBasedSportIndexToken(raw: string): number | null {
  const foldedRaw = asciiFold((raw || '').trim())
  if (!foldedRaw) return null
  // Accept natural spoken variants for sport picks: "the first one", "second", "troisieme", etc.
  if (/\b(first|premier|premiere|1st|1er|1re|1ere)\b/.test(foldedRaw)) return 1
  if (/\b(second|deuxieme|2nd|2e|2eme)\b/.test(foldedRaw)) return 2
  if (/\b(third|troisieme|3rd|3e|3eme)\b/.test(foldedRaw)) return 3
  if (/\b(fourth|quatrieme|4th|4e|4eme)\b/.test(foldedRaw)) return 4
  if (/\b(fifth|cinquieme|5th|5e|5eme)\b/.test(foldedRaw)) return 5
  if (/\b(sixth|sixieme|6th|6e|6eme)\b/.test(foldedRaw)) return 6
  if (/\b(seventh|septieme|7th|7e|7eme)\b/.test(foldedRaw)) return 7
  if (/\b(eighth|huitieme|8th|8e|8eme)\b/.test(foldedRaw)) return 8
  if (/\b(ninth|neuvieme|9th|9e|9eme)\b/.test(foldedRaw)) return 9
  if (/\b(tenth|dixieme|10th|10e|10eme)\b/.test(foldedRaw)) return 10

  const compact = compactOrdinalCandidate(raw)
  if (!compact) return null
  const t = asciiFold(compact)

  if (/^\d+$/.test(t)) {
    const idx = parseInt(t, 10)
    return Number.isFinite(idx) && idx >= 1 ? idx : null
  }

  const enOrd = t.match(/^(\d+)(st|nd|rd|th)$/i)
  if (enOrd) {
    const idx = parseInt(enOrd[1], 10)
    return Number.isFinite(idx) && idx >= 1 ? idx : null
  }

  // French “premier”: 1er, 1re, 1ère — suffix “er/re/ere” must come before the generic (n)e rule.
  if (/^1(?:er|re|ere)$/i.test(t)) {
    return 1
  }

  const frOrd = t.match(/^(\d+)(?:eme|e)$/i)
  if (frOrd) {
    const idx = parseInt(frOrd[1], 10)
    return Number.isFinite(idx) && idx >= 1 ? idx : null
  }

  return null
}

function parseOneBasedLocationIndexToken(raw: string): number | null {
  const folded = asciiFold((raw || '').trim())
  if (!folded) return null
  // Accept natural spoken variants: "second one", "the second", "deuxieme", etc.
  if (/\b(first|premier|premiere|1st|1er|1re|1ere)\b/.test(folded)) return 1
  if (/\b(second|deuxieme|2nd|2e|2eme)\b/.test(folded)) return 2
  if (/\b(third|troisieme|3rd|3e|3eme)\b/.test(folded)) return 3
  if (/\b(fourth|quatrieme|4th|4e|4eme)\b/.test(folded)) return 4
  if (/\b(fifth|cinquieme|5th|5e|5eme)\b/.test(folded)) return 5
  if (/\b(sixth|sixieme|6th|6e|6eme)\b/.test(folded)) return 6
  if (/\b(seventh|septieme|7th|7e|7eme)\b/.test(folded)) return 7
  if (/\b(eighth|huitieme|8th|8e|8eme)\b/.test(folded)) return 8
  if (/\b(ninth|neuvieme|9th|9e|9eme)\b/.test(folded)) return 9
  if (/\b(tenth|dixieme|10th|10e|10eme)\b/.test(folded)) return 10
  return parseOneBasedSportIndexToken(folded)
}

/** Text inside parentheses — often STT puts the ordinal hint here, e.g. ``100 Hospital (the second option)``. */
function extractParentheticalPhrases(text: string): string[] {
  const out: string[] = []
  const re = /\(([^)]+)\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) != null) {
    const inner = (m[1] || '').trim()
    if (inner) out.push(inner)
  }
  return out
}

/**
 * 0-based list index from phrases like ``option 2``, ``the second option``, ``number 3``,
 * including text inside parentheses (disambiguation after a name fragment).
 */
function zeroBasedListIndexFromChoiceUtterance(raw: string): number | null {
  const trimmed = (raw || '').trim()
  if (!trimmed) return null
  for (const inner of extractParentheticalPhrases(trimmed)) {
    const z = parseOrdinalChoice(inner)
    if (z != null) return z
  }
  return parseOrdinalChoice(trimmed)
}

/** Strip parenthetical segments for looser name-only matching (ordinals live in parens). */
function stripParentheticalsForNameMatch(text: string): string {
  return (text || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Match when the user gives several words/digits that should all appear in the location name
 * (e.g. ``100 hospital`` vs API ``100 Main Hospital``).
 */
function matchLocationByAllSignificantTokens(userKey: string, options: LocationPick[]): LocationPick | undefined {
  const key = normNameKey(userKey)
  if (!key) return undefined
  const parts = key.split(/\s+/).filter(Boolean)
  const significant = parts.filter((p) => p.length >= 2 || /^\d+$/.test(p))
  if (significant.length === 0) return undefined

  const hits: LocationPick[] = []
  for (const o of options) {
    const n = normNameKey(o.locationName)
    if (!n) continue
    if (significant.every((p) => locationSignificantTokenMatches(n, p))) hits.push(o)
  }
  if (hits.length === 0) return undefined
  hits.sort((a, b) => normNameKey(b.locationName).length - normNameKey(a.locationName).length)
  return hits[0]
}

function matchFieldByName(
  token: string,
  options: SportsCertificateFieldOption[],
): SportsCertificateFieldOption | undefined {
  const key = normNameKey(token)
  if (!key) return undefined
  for (const o of options) {
    if (normNameKey(o.field_name) === key) return o
  }
  for (const o of options) {
    const fn = normNameKey(o.field_name)
    if (fn.includes(key) || key.includes(stripArticlePrefix(fn))) return o
  }
  for (const o of options) {
    const fn = normNameKey(o.field_name)
    if (key.length >= 3 && (fn.includes(key) || key.includes(fn))) return o
  }
  return undefined
}

export type ResolveSportSelectionResult = {
  selected: SportFieldPick[]
  invalidNames: string[]
  outOfRangeIndices: { oneBased: number; max: number }[]
  hadAnyToken: boolean
}

/**
 * MediKaLLink field list includes a comments row (e.g. “Commentaire”) — not a sport; omit from picks and numbering.
 */
export function sportsCertificateSelectableFieldOptions(
  options: SportsCertificateFieldOption[],
): SportsCertificateFieldOption[] {
  const cid = getSportsCertificateCommentsFieldId()
  return options.filter((o) => Number(o.field_id) !== Number(cid))
}

/**
 * Resolve sports from spoken/typed text against the API field list (names or 1-based indices).
 */
export function resolveSportSelection(
  text: string,
  options: SportsCertificateFieldOption[],
): ResolveSportSelectionResult {
  const selectable = sportsCertificateSelectableFieldOptions(options)
  const out: SportFieldPick[] = []
  const invalidNames: string[] = []
  const outOfRangeIndices: { oneBased: number; max: number }[] = []
  const seen = new Set<number>()
  const n = selectable.length
  const tokens = splitSelectionTokens((text || '').trim())
  const hadAnyToken = tokens.length > 0

  for (const raw of tokens) {
    const t = raw.trim()
    if (!t) continue
    const zbTok = zeroBasedListIndexFromChoiceUtterance(t)
    if (zbTok != null) {
      const parsedIdx = zbTok + 1
      if (parsedIdx >= 1 && parsedIdx <= n) {
        const o = selectable[parsedIdx - 1]
        if (o && !seen.has(o.field_id)) {
          seen.add(o.field_id)
          out.push({ fieldId: o.field_id, fieldName: o.field_name })
        }
      } else {
        outOfRangeIndices.push({ oneBased: parsedIdx, max: n })
      }
      continue
    }
    const parsedIdx = parseOneBasedSportIndexToken(t)
    if (parsedIdx != null) {
      if (parsedIdx >= 1 && parsedIdx <= n) {
        const o = selectable[parsedIdx - 1]
        if (o && !seen.has(o.field_id)) {
          seen.add(o.field_id)
          out.push({ fieldId: o.field_id, fieldName: o.field_name })
        }
      } else {
        outOfRangeIndices.push({ oneBased: parsedIdx, max: n })
      }
      continue
    }
    const m = matchFieldByName(t, selectable)
    if (m) {
      if (!seen.has(m.field_id)) {
        seen.add(m.field_id)
        out.push({ fieldId: m.field_id, fieldName: m.field_name })
      }
    } else {
      invalidNames.push(t)
    }
  }

  return { selected: out, invalidNames, outOfRangeIndices, hadAnyToken }
}

export function formatNumberedSportsList(
  options: SportsCertificateFieldOption[],
  fr: boolean,
): string {
  const rows = sportsCertificateSelectableFieldOptions(options)
  const lines = rows.map((o, i) => `${i + 1}. ${o.field_name}`)
  const intro = fr
    ? 'Choisissez un ou plusieurs sports : noms ou numéros, ou appuyez sur les cartes (coche = sélectionné), puis Continuer.'
    : 'Choose one or more sports — names or numbers, or tap the cards (tick = selected), then Continue.'
  return `${lines.join('\n')}\n\n${intro}`
}

export function formatNumberedLocationList(options: LocationPick[], fr: boolean): string {
  const lines = options.map((o, i) => `${i + 1}. ${o.locationName}`)
  const intro = fr
    ? 'Choisissez une localisation : tapez ou dites 1 ou 2, premier / deuxième, le nom, ou appuyez sur une carte ci-dessous.'
    : 'Choose a location — say or type 1 or 2, first or second, the name, or tap a card below.'
  return `${lines.join('\n')}\n\n${intro}`
}

export function resolveLocationSelection(
  text: string,
  options: LocationPick[],
): {
  selected?: LocationPick
  outOfRangeIndex?: { oneBased: number; max: number }
  invalidNames: string[]
  hadAnyToken: boolean
} {
  const rawText = (text || '').trim()
  const foldedText = asciiFold(rawText)
  const tokens = splitSelectionTokens(rawText)
  const hadAnyToken = tokens.length > 0
  if (!hadAnyToken) return { invalidNames: [], hadAnyToken: false }

  const pickByOneBased = (oneBased: number):
    | { selected: LocationPick; invalidNames: string[]; hadAnyToken: true }
    | {
        outOfRangeIndex: { oneBased: number; max: number }
        invalidNames: string[]
        hadAnyToken: true
      } => {
    if (oneBased >= 1 && oneBased <= options.length) {
      return { selected: options[oneBased - 1], invalidNames: [], hadAnyToken: true }
    }
    return {
      outOfRangeIndex: { oneBased, max: options.length },
      invalidNames: [],
      hadAnyToken: true,
    }
  }

  // Ordinals / "option N" / parenthetical hints — must run before fuzzy name match on the full line
  // so phrases like ``100 Hospital (the second option)`` resolve by position, not by stray digits.
  const zbFull = zeroBasedListIndexFromChoiceUtterance(rawText)
  if (zbFull != null) {
    return pickByOneBased(zbFull + 1)
  }

  // First pass on full utterance helps with voice phrases like "choose the second one".
  const wholeIdx = parseOneBasedLocationIndexToken(foldedText)
  if (wholeIdx != null) {
    return pickByOneBased(wholeIdx)
  }

  const strippedForName = stripParentheticalsForNameMatch(rawText)
  const textKey = normNameKey(strippedForName)
  if (textKey) {
    const exactWhole = options.find((o) => normNameKey(o.locationName) === textKey)
    if (exactWhole) return { selected: exactWhole, invalidNames: [], hadAnyToken: true }
    // Multi-token / digit + name (e.g. ``100 hospital``) before loose substring match, so a
    // short option like ``100`` does not win over ``100 Main Hospital``.
    const tokenWhole = matchLocationByAllSignificantTokens(strippedForName, options)
    if (tokenWhole) return { selected: tokenWhole, invalidNames: [], hadAnyToken: true }
    const inclusiveWhole = options.find((o) => {
      const n = normNameKey(o.locationName)
      return n.includes(textKey) || textKey.includes(n)
    })
    if (inclusiveWhole) return { selected: inclusiveWhole, invalidNames: [], hadAnyToken: true }
  }

  const foldedStripped = asciiFold(strippedForName)

  for (const raw of tokens) {
    const t = raw.trim()
    if (!t) continue
    const zbTok = zeroBasedListIndexFromChoiceUtterance(t)
    if (zbTok != null) {
      return pickByOneBased(zbTok + 1)
    }
    const idx = parseOneBasedLocationIndexToken(asciiFold(t))
    if (idx != null) {
      return pickByOneBased(idx)
    }
    const key = normNameKey(stripParentheticalsForNameMatch(t))
    if (!key) continue
    const exact = options.find((o) => normNameKey(o.locationName) === key)
    if (exact) return { selected: exact, invalidNames: [], hadAnyToken: true }
    const fuzzy = options.find((o) => {
      const n = normNameKey(o.locationName)
      return n.includes(key) || key.includes(n) || foldedStripped.includes(n) || foldedText.includes(n)
    })
    if (fuzzy) return { selected: fuzzy, invalidNames: [], hadAnyToken: true }
    const looseTok = matchLocationByAllSignificantTokens(t, options)
    if (looseTok) return { selected: looseTok, invalidNames: [], hadAnyToken: true }
    return { invalidNames: [t], hadAnyToken: true }
  }
  return { invalidNames: [], hadAnyToken: true }
}

export function sportPicksFromSummary(summary: SportsNocCertificatePayload): SportFieldPick[] {
  const out: SportFieldPick[] = []
  for (const name of summary.sports) {
    const o = summary.sportsFieldOptions.find((x) => x.field_name === name)
    if (o) out.push({ fieldId: o.field_id, fieldName: o.field_name })
  }
  return out
}

export function buildSportsNocPayload(
  ctx: PatientContext | Record<string, unknown> | undefined,
  selectedSports: SportFieldPick[],
  comments: string | null,
  sportsFieldOptions: SportsCertificateFieldOption[],
  locationIdOverride?: number,
): SportsNocCertificatePayload {
  const p = patientFromContext(ctx)
  const locationId = Number(locationIdOverride)
  if (!Number.isFinite(locationId) || locationId <= 0) {
    throw new Error('Missing location id from API response.')
  }
  const commentFieldId = getSportsCertificateCommentsFieldId()
  const sportFields = selectedSports.map((s) => ({ id: s.fieldId, value: s.fieldName }))
  const apiFields = [...sportFields]
  const c = comments != null && String(comments).trim() ? String(comments).trim() : null
  if (c) {
    apiFields.push({ id: commentFieldId, value: c })
  }
  return {
    intent: SPORTS_NOC_INTENT,
    certificate_type: 'sport_no_objection',
    patient: { id: p.id, name: p.name },
    sports: selectedSports.map((s) => s.fieldName),
    comments: c,
    locationId,
    apiFields,
    sportsFieldOptions: sportsFieldOptions.map((r) => ({ ...r })),
  }
}

/**
 * After the certificate summary is shown, detect voice/typed phrases that mean
 * "edit sports" or "edit comments" (EN/FR). Requires an explicit edit-style verb
 * so phrases like "no comments" do not match.
 */
export function parseSportsNocReviewEditIntent(text: string): 'location' | 'sports' | 'comments' | null {
  const t = asciiFold((text || '').trim())
  if (!t) return null

  const editVerbEn = String.raw`(?:edit|change|update|modify|adjust|fix|correct)`
  const editVerbFr = String.raw`(?:modifier|changer|mettre\s+a\s+jour|corriger|reprendre)`

  if (
    new RegExp(`\\b${editVerbEn}\\s+(?:the|my|our|a|an)?\\s*(?:location|site)\\b`).test(t) ||
    new RegExp(`\\b${editVerbEn}\\s+location\\b`).test(t) ||
    new RegExp(`\\b${editVerbFr}\\s+(?:la|le)?\\s*(?:localisation|site|lieu)\\b`).test(t) ||
    new RegExp(`\\b(?:localisation|site|lieu)\\s+(?:please\\s+)?${editVerbFr}\\b`).test(t)
  ) {
    return 'location'
  }

  // Voice / typed short phrases first: "edit the comments", "modifier les commentaires", …
  if (
    new RegExp(`\\b${editVerbEn}\\s+(?:the|my|our|a|an)?\\s*comments?\\b`).test(t) ||
    new RegExp(`\\b${editVerbEn}\\s+comments?\\b`).test(t) ||
    new RegExp(`\\bcomments?\\s+(?:please\\s+)?${editVerbEn}\\b`).test(t) ||
    /\b(?:re)?write\s+(?:the|my|our)?\s*comments?\b/.test(t) ||
    new RegExp(`\\b${editVerbFr}\\s+(?:les|la)?\\s*commentaires?\\b`).test(t) ||
    new RegExp(`\\bcommentaires?\\s+(?:please\\s+)?${editVerbFr}\\b`).test(t)
  ) {
    return 'comments'
  }

  if (
    new RegExp(`\\b${editVerbEn}\\s+(?:the|my|our|a|an)?\\s*(?:sports?|games?|activities)\\b`).test(t) ||
    new RegExp(`\\b${editVerbEn}\\s+(?:sports?|games?|activities)\\b`).test(t) ||
    new RegExp(`\\b(?:sports?|games?|activities)\\s+(?:please\\s+)?${editVerbEn}\\b`).test(t) ||
    /\b(?:re)?write\s+(?:the|my|our)?\s*(?:sports?|games?|activities)\b/.test(t) ||
    new RegExp(`\\b${editVerbFr}\\s+(?:les|la)?\\s*(?:sports?|jeux|activites)\\b`).test(t) ||
    new RegExp(`\\b(?:sports?|jeux|activites)\\s+(?:please\\s+)?${editVerbFr}\\b`).test(t)
  ) {
    return 'sports'
  }

  const editEn = `${editVerbEn}\\b`
  const editFr = `${editVerbFr}\\b`
  const sportsGamesEn = String.raw`(?:sports?|games?|activities)\b`
  const sportsGamesFr = String.raw`(?:sports?|games?|jeux|activites)\b`

  if (
    new RegExp(`${editEn}.*\\b(?:location|site)\\b`).test(t) ||
    new RegExp(`\\b(?:location|site)\\b.*${editEn}`).test(t) ||
    new RegExp(`${editFr}.*\\b(?:localisation|site|lieu)\\b`).test(t) ||
    new RegExp(`\\b(?:localisation|site|lieu)\\b.*${editFr}`).test(t)
  ) {
    return 'location'
  }

  if (
    new RegExp(`${editEn}.*\\bcomments?\\b`).test(t) ||
    new RegExp(`\\bcomments?\\b.*${editEn}`).test(t) ||
    new RegExp(`${editFr}.*\\bcommentaires?\\b`).test(t) ||
    new RegExp(`\\bcommentaires?\\b.*${editFr}`).test(t)
  ) {
    return 'comments'
  }

  if (
    new RegExp(`${editEn}.*${sportsGamesEn}`).test(t) ||
    new RegExp(`${sportsGamesEn}.*${editEn}`).test(t) ||
    new RegExp(`${editFr}.*${sportsGamesFr}`).test(t) ||
    new RegExp(`${sportsGamesFr}.*${editFr}`).test(t)
  ) {
    return 'sports'
  }

  return null
}

/** Latest actionable generate-confirm summary card (preview + Yes/No). */
export function findSportsNocGenerateConfirmMessageId(
  messages: readonly ChatMessage[],
): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || m.historySnapshot) continue
    if (m.sportsNocFlowCancelled) continue
    if (
      m.sportsNocGenerateConfirm ||
      (m.sportsNocAwaitingField === 'generate_confirm' &&
        m.sportsNocSummary &&
        m.sportsNocYesNoPrompt?.kind === 'generate_certificate')
    ) {
      return m.id
    }
  }
  return undefined
}

export function trySportsNocGenerateConfirmReviewEdit(
  text: string,
  messages: readonly ChatMessage[],
): { messageId: string; field: 'location' | 'sports' | 'comments' } | null {
  const field = parseSportsNocReviewEditIntent(text)
  if (!field) return null
  const messageId = findSportsNocGenerateConfirmMessageId(messages)
  if (messageId) return { messageId, field }
  // Fallback: still honor edit command on the latest visible summary card.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || m.historySnapshot || m.sportsNocFlowCancelled) continue
    if (m.sportsNocSummary) return { messageId: m.id, field }
  }
  return null
}

export function sportsNocReviewEditWorkflowAction(
  field: 'location' | 'sports' | 'comments',
): 'edit_location' | 'edit_sports' | 'edit_comments' {
  if (field === 'location') return 'edit_location'
  if (field === 'sports') return 'edit_sports'
  return 'edit_comments'
}

export type SportsNocYesNoKind = 'generate_certificate' | 'mail_certificate'

/** In-chat action cards for sports certificate mail send confirmation. */
export function sportsNocMailConfirmLabels(fr: boolean): {
  yes: string
  no: string
  ariaLabel: string
} {
  return fr
    ? {
        yes: 'Oui, envoyer par courriel',
        no: 'Non, annuler',
        ariaLabel: 'Envoyer le certificat par courriel ?',
      }
    : {
        yes: 'Yes, mail it',
        no: 'No, cancel it',
        ariaLabel: 'Mail this certificate?',
      }
}

export type SportsNocAwaitingField =
  | 'location'
  | 'sports'
  | 'comments'
  | 'generate_confirm'
  | 'preview_open'
  | 'mail_confirm'

const SPORTS_NOC_AWAITING_FIELDS: readonly SportsNocAwaitingField[] = [
  'location',
  'sports',
  'comments',
  'generate_confirm',
  'preview_open',
  'mail_confirm',
]

export function parseSportsNocAwaitingField(raw: unknown): SportsNocAwaitingField | undefined {
  const s = String(raw ?? '').trim() as SportsNocAwaitingField
  return SPORTS_NOC_AWAITING_FIELDS.includes(s) ? s : undefined
}

export type SportsNocMailPreviewPayload = {
  documentType: string
  mailSubject: string
  mailBody: string
  recipientName: string
  recipientType: string
}

export type SportsNocAssistantMetadataFields = {
  sportsNocFrench?: boolean
  sportsNocAwaitingField?: SportsNocAwaitingField
  sportsNocNeedsClarification?: boolean
  sportsNocSummary?: SportsNocCertificatePayload
  sportsNocYesNoPrompt?: { kind: SportsNocYesNoKind }
  sportsNocCertificateGenerated?: boolean
  sportsNocMailPreview?: SportsNocMailPreviewPayload
  sportsNocLocationOptions?: SportsNocLocationOption[]
  sportsNocLocationPickerStyle?: 'dropdown' | 'cards'
  sportsNocCurrentLocationId?: number
  sportsNocCommentsDraft?: string
  sportsNocEditUiOnly?: boolean
  sportsNocSportOptions?: SportsNocSportOption[]
  sportsNocSportSelectedIds?: number[]
  sportsNocPreviewUpdateInPlace?: boolean
  sportsNocSummaryUpdateInPlace?: boolean
}

export type SportsNocSummaryEditField = 'location' | 'sports' | 'comments'

export type SportsNocEditContext = {
  awaitingField: SportsNocSummaryEditField
  locationOptions: SportsNocLocationOption[]
  sportOptions: SportsNocSportOption[]
  pendingSelectedSportIds: number[]
  currentLocationId?: number
  currentComments?: string
}

export function normalizeSportsNocEditContextFromApi(raw: unknown): SportsNocEditContext | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const fieldRaw = String(o.awaiting_field ?? o.awaitingField ?? '').trim()
  const awaitingField =
    fieldRaw === 'location' || fieldRaw === 'sports' || fieldRaw === 'comments'
      ? (fieldRaw as SportsNocSummaryEditField)
      : null
  if (!awaitingField) return null
  const locationOptions = normalizeSportsNocLocationOptionsFromApi(
    o.location_options ?? o.locationOptions,
  )
  const sportOptions = normalizeSportsNocSportOptionsFromApi(
    o.sport_options ?? o.sportOptions ?? o.sports_field_options,
  )
  const pendingRaw = o.pending_selected_sport_ids ?? o.pendingSelectedSportIds
  const pendingSelectedSportIds = Array.isArray(pendingRaw)
    ? pendingRaw.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
    : []
  const curLoc = Number(o.current_location_id ?? o.currentLocationId)
  const currentLocationId = Number.isFinite(curLoc) && curLoc > 0 ? curLoc : undefined
  const curComments = o.current_comments ?? o.currentComments
  return {
    awaitingField,
    locationOptions,
    sportOptions,
    pendingSelectedSportIds,
    currentLocationId,
    currentComments: curComments != null ? String(curComments) : undefined,
  }
}

export function normalizeSportsNocLocationOptionsFromApi(raw: unknown): SportsNocLocationOption[] {
  if (!Array.isArray(raw)) return []
  const out: SportsNocLocationOption[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    const locationId = Number(o.location_id ?? o.locationId)
    const label = String(o.label ?? o.location_name ?? o.locationName ?? '').trim()
    if (!Number.isFinite(locationId) || locationId <= 0 || !label) continue
    out.push({ locationId, label })
  }
  return out
}

export function findPendingSportsNocLocationOptions(
  messages: readonly ChatMessage[],
): { messageId: string; options: SportsNocLocationOption[] } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || isRestoredHistoryChatMessage(m)) continue
    if (m.sportsNocFlowCancelled) return undefined
    const opts = m.sportsNocLocationOptions
    if (
      m.uiType === 'sports_noc_location_options' &&
      opts &&
      opts.length > 0 &&
      !m.sportsNocLocationOptionsResolved
    ) {
      return { messageId: m.id, options: opts }
    }
  }
  return undefined
}

export function markSportsNocLocationOptionsResolvedInMessages(
  messages: ChatMessage[],
): ChatMessage[] {
  const pending = findPendingSportsNocLocationOptions(messages)
  if (!pending) return messages
  return messages.map((m) =>
    m.id === pending.messageId ? { ...m, sportsNocLocationOptionsResolved: true } : m,
  )
}

export function formatSportsNocLocationSelectionMessage(
  opt: SportsNocLocationOption,
  _fr?: boolean,
): string {
  return opt.label
}

export function normalizeSportsNocSportOptionsFromApi(raw: unknown): SportsNocSportOption[] {
  if (!Array.isArray(raw)) return []
  const cid = getSportsCertificateCommentsFieldId()
  const out: SportsNocSportOption[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    const fieldId = Number(o.field_id ?? o.fieldId)
    const label = String(o.label ?? o.field_name ?? o.fieldName ?? '').trim()
    if (!Number.isFinite(fieldId) || fieldId <= 0 || fieldId === cid || !label) continue
    out.push({ fieldId, label })
  }
  return out
}

export function findPendingSportsNocSportOptions(
  messages: readonly ChatMessage[],
): { messageId: string; options: SportsNocSportOption[] } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || isRestoredHistoryChatMessage(m)) continue
    if (m.sportsNocFlowCancelled) return undefined
    const opts = m.sportsNocSportOptions
    if (
      m.uiType === 'sports_noc_sports_options' &&
      opts &&
      opts.length > 0 &&
      !m.sportsNocSportOptionsResolved
    ) {
      return { messageId: m.id, options: opts }
    }
  }
  return undefined
}

export function toggleSportsNocSportSelectedInMessages(
  messages: ChatMessage[],
  messageId: string,
  fieldId: number,
): ChatMessage[] {
  return messages.map((m) => {
    if (m.id !== messageId) return m
    const cur = new Set(m.sportsNocSportSelectedIds ?? [])
    if (cur.has(fieldId)) cur.delete(fieldId)
    else cur.add(fieldId)
    return { ...m, sportsNocSportSelectedIds: [...cur] }
  })
}

export function buildSportsNocSportsSubmitText(message: ChatMessage): string | null {
  const opts = message.sportsNocSportOptions ?? []
  const ids = new Set(message.sportsNocSportSelectedIds ?? [])
  const labels = opts.filter((o) => ids.has(o.fieldId)).map((o) => o.label)
  if (!labels.length) return null
  return labels.join(', ')
}

export function markSportsNocSportOptionsResolvedInMessages(
  messages: ChatMessage[],
): ChatMessage[] {
  const pending = findPendingSportsNocSportOptions(messages)
  if (!pending) return messages
  return messages.map((m) =>
    m.id === pending.messageId ? { ...m, sportsNocSportOptionsResolved: true } : m,
  )
}

/** Default sports certificate mail preview (EN). */
export function sportsNocMailPreviewDefaults(
  patientName: string,
  fr?: boolean,
): SportsNocMailPreviewPayload {
  const name = (patientName || '').trim() || 'Patient'
  if (fr) {
    return {
      documentType: 'Certificat sport',
      mailSubject: 'Certificat sport',
      mailBody: 'Certificat sport ci-joint.',
      recipientName: name,
      recipientType: 'patient',
    }
  }
  return {
    documentType: 'Sports certificate',
    mailSubject: 'Sports certificate',
    mailBody: 'Sports certificate enclosed',
    recipientName: name,
    recipientType: 'patient',
  }
}

function normalizeSportsNocMailPreviewFromApi(raw: unknown): SportsNocMailPreviewPayload | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const o = raw as Record<string, unknown>
  const recipientName = String(o.recipientName ?? o.recipient_name ?? '').trim()
  const mailSubject = String(o.mailSubject ?? o.mail_subject ?? '').trim()
  const mailBody = String(o.mailBody ?? o.mail_body ?? '').trim()
  const documentType = String(o.documentType ?? o.document_type ?? mailSubject).trim()
  if (!recipientName && !mailSubject && !mailBody) return undefined
  return {
    documentType: documentType || 'Sports certificate',
    mailSubject: mailSubject || documentType || 'Sports certificate',
    mailBody: mailBody || `${documentType || 'Sports certificate'} enclosed.`,
    recipientName: recipientName || 'Patient',
    recipientType: String(o.recipientType ?? o.recipient_type ?? 'patient').trim() || 'patient',
  }
}

/** Normalize backend ``sports_noc.summary`` for the summary card (tolerates snake_case keys). */
export function normalizeSportsNocSummaryFromApi(raw: unknown): SportsNocCertificatePayload | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const o = raw as Record<string, unknown>
  const patientRaw = o.patient
  const patient =
    patientRaw && typeof patientRaw === 'object' && !Array.isArray(patientRaw)
      ? {
          id: String((patientRaw as Record<string, unknown>).id ?? '').trim(),
          name: String((patientRaw as Record<string, unknown>).name ?? '').trim(),
        }
      : { id: '', name: '' }
  const sportsRaw = o.sports
  const sports = Array.isArray(sportsRaw)
    ? sportsRaw.map((s) => String(s ?? '').trim()).filter(Boolean)
    : []
  const apiFieldsRaw = o.apiFields ?? o.api_fields
  const apiFields: SportsNocCertificatePayload['apiFields'] = Array.isArray(apiFieldsRaw)
    ? apiFieldsRaw
        .map((f) => {
          if (!f || typeof f !== 'object') return null
          const fr = f as Record<string, unknown>
          const id = Number(fr.id ?? fr.field_id)
          const value = String(fr.value ?? fr.field_name ?? '').trim()
          if (!Number.isFinite(id) || !value) return null
          return { id, value }
        })
        .filter((x): x is { id: number; value: string } => x != null)
    : []
  const optionsRaw = o.sportsFieldOptions ?? o.sports_field_options
  const sportsFieldOptions: SportsCertificateFieldOption[] = Array.isArray(optionsRaw)
    ? optionsRaw
        .map((f) => {
          if (!f || typeof f !== 'object') return null
          const fr = f as Record<string, unknown>
          const field_id = Number(fr.field_id ?? fr.id)
          const field_name = String(fr.field_name ?? fr.value ?? '').trim()
          if (!Number.isFinite(field_id) || !field_name) return null
          return {
            field_id,
            field_type: Number(fr.field_type ?? 0),
            field_name,
            ...(fr.field_type_txt != null ? { field_type_txt: String(fr.field_type_txt) } : {}),
          }
        })
        .filter((x): x is SportsCertificateFieldOption => x != null)
    : []
  const commentsRaw = o.comments
  const comments =
    commentsRaw == null || commentsRaw === ''
      ? null
      : String(commentsRaw).trim() || null
  const locationId = Number(o.locationId ?? o.location_id ?? 0)
  const locationNameRaw = o.locationName ?? o.location_name
  const locationName =
    locationNameRaw == null || locationNameRaw === ''
      ? null
      : String(locationNameRaw).trim() || null
  if (sports.length === 0 && apiFields.length === 0 && !locationName) {
    return undefined
  }
  return {
    intent: SPORTS_NOC_INTENT,
    certificate_type: 'sport_no_objection',
    patient,
    sports,
    comments,
    locationId: Number.isFinite(locationId) ? locationId : 0,
    locationName,
    apiFields,
    sportsFieldOptions,
  }
}

/** Map ``metadata.sports_noc`` or flattened wire fields onto chat message props. */
export function sportsNocFieldsFromAssistantMetadata(
  md: Record<string, unknown> | undefined,
): SportsNocAssistantMetadataFields {
  const out: SportsNocAssistantMetadataFields = {}
  if (!md) return out

  if (md.sportsNocFrench === true || md.sports_noc_french === true) out.sportsNocFrench = true
  else if (md.sportsNocFrench === false || md.sports_noc_french === false) out.sportsNocFrench = false

  const awaitingTop = parseSportsNocAwaitingField(md.sportsNocAwaitingField ?? md.sports_noc_awaiting_field)
  if (awaitingTop) out.sportsNocAwaitingField = awaitingTop
  if (md.sportsNocNeedsClarification === true || md.sports_noc_needs_clarification === true) {
    out.sportsNocNeedsClarification = true
  }

  if (md.sportsNocSummary && typeof md.sportsNocSummary === 'object') {
    const summary = normalizeSportsNocSummaryFromApi(md.sportsNocSummary)
    if (summary) out.sportsNocSummary = summary
  }

  if (md.sportsNocCertificateGenerated === true || md.sports_noc_certificate_generated === true) {
    out.sportsNocCertificateGenerated = true
  }

  const mpTop = md.sportsNocMailPreview ?? md.sports_noc_mail_preview
  const mpNormTop = normalizeSportsNocMailPreviewFromApi(mpTop)
  if (mpNormTop) out.sportsNocMailPreview = mpNormTop

  const ynpTop = md.sportsNocYesNoPrompt
  if (ynpTop && typeof ynpTop === 'object') {
    const kind = String((ynpTop as Record<string, unknown>).kind ?? '').trim()
    if (kind === 'generate_certificate' || kind === 'mail_certificate') {
      out.sportsNocYesNoPrompt = { kind }
    }
  }

  const sn =
    md.sports_noc && typeof md.sports_noc === 'object' && !Array.isArray(md.sports_noc)
      ? (md.sports_noc as Record<string, unknown>)
      : md.kind === 'sports_noc' && !md.sports_noc
        ? md
        : null
  if (!sn) return out

  if (sn.french === true) out.sportsNocFrench = true
  else if (sn.french === false) out.sportsNocFrench = false

  const awaitingSn = parseSportsNocAwaitingField(sn.awaiting_field ?? sn.awaitingField)
  if (awaitingSn) out.sportsNocAwaitingField = awaitingSn
  if (sn.needs_clarification === true) out.sportsNocNeedsClarification = true

  const locOpts = normalizeSportsNocLocationOptionsFromApi(
    sn.location_options ?? sn.locationOptions,
  )
  if (locOpts.length) out.sportsNocLocationOptions = locOpts

  const pickerStyle = String(sn.location_picker_style ?? sn.locationPickerStyle ?? '').trim()
  if (pickerStyle === 'dropdown') out.sportsNocLocationPickerStyle = 'dropdown'

  const curLoc = Number(sn.current_location_id ?? sn.currentLocationId)
  if (Number.isFinite(curLoc) && curLoc > 0) out.sportsNocCurrentLocationId = curLoc

  const curComments = sn.current_comments ?? sn.currentComments
  if (curComments != null) {
    out.sportsNocCommentsDraft = String(curComments)
  }

  if (sn.edit_ui_only === true || md.sports_noc_edit_ui_only === true) {
    out.sportsNocEditUiOnly = true
  }

  const sportOpts = normalizeSportsNocSportOptionsFromApi(
    sn.sports_field_options ?? sn.sportsFieldOptions,
  )
  if (sportOpts.length) out.sportsNocSportOptions = sportOpts

  const pendingRaw = sn.pending_selected_sport_ids ?? sn.pendingSelectedSportIds
  if (Array.isArray(pendingRaw)) {
    out.sportsNocSportSelectedIds = pendingRaw
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0)
  }
  if (sn.preview_update_in_place === true || md.sports_noc_preview_update_in_place === true) {
    out.sportsNocPreviewUpdateInPlace = true
  }
  if (sn.summary_update_in_place === true || md.sports_noc_summary_update_in_place === true) {
    out.sportsNocSummaryUpdateInPlace = true
  }

  const summary = normalizeSportsNocSummaryFromApi(sn.summary)
  if (summary) out.sportsNocSummary = summary

  if (sn.certificate_generated === true) out.sportsNocCertificateGenerated = true

  const mpSn = normalizeSportsNocMailPreviewFromApi(sn.mail_preview ?? sn.mailPreview)
  if (mpSn) out.sportsNocMailPreview = mpSn

  const ynp = sn.yes_no_prompt
  if (ynp && typeof ynp === 'object') {
    const kind = String((ynp as Record<string, unknown>).kind ?? '').trim()
    if (kind === 'generate_certificate' || kind === 'mail_certificate') {
      out.sportsNocYesNoPrompt = { kind }
    }
  }
  return out
}

function assistantMessageLooksLikeSportsNocCommentsPrompt(content: unknown): boolean {
  const c = chatContentString(content).toLowerCase()
  return (
    /comment/.test(c) &&
    /(\*\*no\*\*|\*\*non\*\*|say\s+\*?\*?no|dites.{0,16}\*?\*?non|there are none|aucun|sans\s+commentaire|if there are none|s'il n'y en a pas)/.test(
      c,
    )
  )
}

/** Transcript rows from Redis after refresh — not an active server-guided turn. */
export function isRestoredHistoryChatMessage(m: ChatMessage): boolean {
  return m.historySnapshot === true
}

/** Merge Redis workflow snapshot into live messages after Laravel refresh (sport ticks, awaiting field). */
export function applySportsNocWorkflowSnapshotToMessages(
  messages: readonly ChatMessage[],
  snapshot: {
    awaiting_field?: string | null
    pending_selected_sport_ids?: number[]
    french?: boolean
  } | null | undefined,
): ChatMessage[] {
  if (!snapshot?.awaiting_field) return [...messages]
  const pending = Array.isArray(snapshot.pending_selected_sport_ids)
    ? snapshot.pending_selected_sport_ids.filter((n) => Number.isFinite(n) && n > 0)
    : []
  const awaiting = parseSportsNocAwaitingField(snapshot.awaiting_field)
  if (!awaiting) return [...messages]
  let patched = false
  return messages.map((m) => {
    if (patched || m.role !== 'assistant') return m
    if (awaiting === 'sports' && m.uiType !== 'sports_noc_sports_options') return m
    if (awaiting === 'location' && m.uiType !== 'sports_noc_location_options') return m
    patched = true
    const next: ChatMessage = {
      ...m,
      sportsNocAwaitingField: awaiting,
      sportsNocNeedsClarification: true,
    }
    if (snapshot.french === true) next.sportsNocFrench = true
    else if (snapshot.french === false) next.sportsNocFrench = false
    if (awaiting === 'sports') next.sportsNocSportSelectedIds = pending
    return next
  })
}

/** True when an active sports NOC step still expects input (use hosted /chat/command, not host sendMessage). */
export function isSportsNocGuidedFlowInProgress(messages: readonly ChatMessage[]): boolean {
  if (sportsNocFlowWasCancelled(messages)) return false
  return findPendingSportsNocClarification(messages) != null || findPendingSportsNocYesNo(messages) != null
}

function assistantMessageLooksLikeSportsNocCollectStep(content: unknown): boolean {
  const c = chatContentString(content).toLowerCase()
  if (assistantMessageLooksLikeSportsNocCommentsPrompt(c)) return true
  return /choose sports|pick sports|choisissez.{0,24}sport|choose a location|pick a location|choisissez une localisation/.test(
    c,
  )
}

/** True while the guided flow is still collecting location / sports / comments (before summary Yes/No). */
export function isInSportsNocGuidedCollectPhase(messages: readonly ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || isRestoredHistoryChatMessage(m)) continue
    if (m.sportsNocFlowCancelled) return false
    if (m.sportsNocYesNoPrompt && !m.sportsNocYesNoResolved) return false
    if (m.sportsNocSummary) return false
    if (m.sportsNocNeedsClarification && m.sportsNocAwaitingField) {
      return true
    }
  }
  return false
}

/** Latest assistant sports NOC row that still expects typed/voice follow-up (not Yes/No cards). */
export function findPendingSportsNocClarification(
  messages: readonly ChatMessage[],
): SportsNocAwaitingField | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'user' && chatContentTrim(m.content).length > 0) return null
    if (m.role !== 'assistant' || isRestoredHistoryChatMessage(m)) continue
    if (m.sportsNocFlowCancelled) return null
    if (m.sportsNocNeedsClarification && m.sportsNocAwaitingField) {
      return m.sportsNocAwaitingField
    }
  }
  return null
}

/**
 * While sports NOC is awaiting input, do not treat short yes/no as patient-switch answers
 * (e.g. "no" / "none" at the comments step after an earlier chart switch).
 */
export function sportsNocShouldBypassPatientSwitch(
  messages: readonly ChatMessage[],
  text: string,
): boolean {
  const trimmed = (text || '').trim()
  if (!trimmed) return false
  if (findPendingSportsNocClarification(messages)) return true
  if (findPendingSportsNocYesNo(messages)) {
    return parseSportsNocYesNo(trimmed) !== null
  }
  return false
}

/** Mark the open sports NOC Yes/No card resolved before sending the user's answer. */
export function markSportsNocYesNoResolvedInMessages(messages: ChatMessage[]): ChatMessage[] {
  const pending = findPendingSportsNocYesNo(messages)
  if (!pending) return messages
  return messages.map((m) =>
    m.id === pending.messageId ? { ...m, sportsNocYesNoResolved: true } : m,
  )
}

/** Latest in-chat Yes/No under a sports NOC summary that is still awaiting an answer. */
export function findPendingSportsNocYesNo(
  messages: readonly ChatMessage[],
): { messageId: string; kind: SportsNocYesNoKind } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (isRestoredHistoryChatMessage(m)) continue
    if (m.role === 'assistant' && m.sportsNocYesNoPrompt && !m.sportsNocYesNoResolved) {
      return { messageId: m.id, kind: m.sportsNocYesNoPrompt.kind }
    }
  }
  return undefined
}

/** Typed or voice yes/no for sports NOC follow-up cards. */
export function parseSportsNocYesNo(text: string): 'yes' | 'no' | null {
  const t = asciiFold((text || '').trim())
  if (!t) return null
  if (
    /^(mail it|send mail|send it|email it|e-mail it|oui envoyer|envoyer|envoie-le|envoie la)\b/.test(
      t,
    )
  ) {
    return 'yes'
  }
  if (/^(cancel it|dont send|do not send|annuler|n envoie pas|ne pas envoyer)\b/.test(t)) {
    return 'no'
  }
  if (/^(yes|yeah|yep|yup|sure|ok|okay|oui|si|please|affirmatif)\b/.test(t) || t === 'y') {
    return 'yes'
  }
  if (/^(no|nope|nah|non|non merci|cancel|annuler)\b/.test(t) || t === 'n') {
    return 'no'
  }
  return null
}

export type SportsNocApiRowLike = {
  role?: string
  text?: string
  metadata?: unknown
  id?: string
}

function parseApiRowMetadata(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : undefined
    } catch {
      return undefined
    }
  }
  return typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : undefined
}

function apiRowIsSportsNocIntentCancelled(row: SportsNocApiRowLike): boolean {
  if (row.role !== 'assistant') return false
  const md = parseApiRowMetadata(row.metadata)
  if (!md) return false
  const kind = String(md.kind ?? '').trim()
  return (
    kind === 'intent_task_cancelled' ||
    md.sports_noc_flow_cancelled === true ||
    md.sportsNocFlowCancelled === true
  )
}

/** True when this /chat/command batch abandoned the sports guided flow for a new command. */
export function apiRowsIndicateSportsNocFlowCancelled(
  rows: readonly SportsNocApiRowLike[],
): boolean {
  return rows.some((r) => apiRowIsSportsNocIntentCancelled(r))
}

/** True when the transcript already contains a sports-flow cancellation assistant row. */
export function sportsNocFlowWasCancelled(messages: readonly ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'assistant' && m.sportsNocFlowCancelled) return true
  }
  return false
}

/** When the server cancels sports NOC for a new command, retire pending Yes/No and prompts. */
export function applySportsNocFlowCancelledToEarlierMessages(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  const flowCancelled = incoming.some(
    (m) => m.role === 'assistant' && m.sportsNocFlowCancelled === true,
  )
  if (!flowCancelled) return prev
  const incomingIds = new Set(incoming.map((m) => m.id))
  return prev.map((m) => {
    if (incomingIds.has(m.id)) return m
    if (m.role !== 'assistant') return m
    if (
      !m.sportsNocNeedsClarification &&
      !m.sportsNocYesNoPrompt &&
      !m.sportsNocSummary &&
      !m.sportsNocGenerateConfirm &&
      !m.sportsNocAwaitingField
    ) {
      return m
    }
    const next: ChatMessage = {
      ...m,
      sportsNocFlowCancelled: true,
      sportsNocNeedsClarification: false,
      sportsNocAwaitingField: undefined,
      sportsNocGenerateConfirm: undefined,
    }
    if (m.uiType === 'sports_noc_location_options') {
      next.sportsNocLocationOptionsResolved = true
    }
    if (m.uiType === 'sports_noc_sports_options') {
      next.sportsNocSportOptionsResolved = true
    }
    if (m.sportsNocYesNoPrompt && !m.sportsNocYesNoResolved) {
      next.sportsNocYesNoResolved = true
    }
    return next
  })
}

function isSportsNocGenerateConfirmCard(m: ChatMessage): boolean {
  return Boolean(
    m.sportsNocGenerateConfirm ||
      (m.sportsNocAwaitingField === 'generate_confirm' &&
        m.sportsNocYesNoPrompt?.kind === 'generate_certificate'),
  )
}

function isSportsNocSportsPickerRefreshMessage(m: ChatMessage): boolean {
  return (
    m.role === 'assistant' &&
    m.sportsNocPreviewUpdateInPlace === true &&
    m.sportsNocAwaitingField === 'sports'
  )
}

function isActiveSportsNocSportPicker(m: ChatMessage): boolean {
  return (
    m.role === 'assistant' &&
    m.uiType === 'sports_noc_sports_options' &&
    m.sportsNocAwaitingField === 'sports' &&
    !m.sportsNocSportOptionsResolved
  )
}

export function applySportsNocClarificationSupersedeToEarlierMessages(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  const incomingIds = new Set(incoming.map((m) => m.id))
  const summaryUpdateInPlace = incoming.some(
    (m) => m.role === 'assistant' && m.sportsNocSummaryUpdateInPlace === true,
  )
  const sportsPickerRefresh = incoming.some(isSportsNocSportsPickerRefreshMessage)
  const advancesFlow = incoming.some(
    (m) =>
      m.role === 'assistant' &&
      (m.sportsNocSummaryUpdateInPlace === true ||
        m.sportsNocSummary != null ||
        m.sportsNocAwaitingField === 'generate_confirm' ||
        m.sportsNocAwaitingField === 'preview_open' ||
        m.sportsNocAwaitingField === 'mail_confirm' ||
        m.sportsNocAwaitingField === 'location' ||
        m.sportsNocAwaitingField === 'sports' ||
        m.sportsNocAwaitingField === 'comments' ||
        Boolean(m.sportsNocYesNoPrompt)),
  )
  if (!advancesFlow) return prev
  return prev.map((m) => {
    if (incomingIds.has(m.id)) return m
    if (m.role !== 'assistant') return m
    // Sport tick refresh updates the same picker in place — keep it actionable.
    if (sportsPickerRefresh && isActiveSportsNocSportPicker(m)) return m
    if (!m.sportsNocNeedsClarification && !m.sportsNocYesNoPrompt) return m
    const next: ChatMessage = { ...m, sportsNocNeedsClarification: false }
    if (
      m.sportsNocAwaitingField === 'location' ||
      m.sportsNocAwaitingField === 'sports' ||
      m.sportsNocAwaitingField === 'comments'
    ) {
      next.sportsNocAwaitingField = undefined
    }
    if (m.uiType === 'sports_noc_location_options') {
      next.sportsNocLocationOptionsResolved = true
    }
    if (m.uiType === 'sports_noc_sports_options') {
      next.sportsNocSportOptionsResolved = true
    }
    if (m.sportsNocYesNoPrompt && !m.sportsNocYesNoResolved) {
      // Inline summary edits refresh the same generate-confirm card; keep Yes/No active.
      if (!(summaryUpdateInPlace && isSportsNocGenerateConfirmCard(m))) {
        next.sportsNocYesNoResolved = true
      }
    }
    return next
  })
}

/** Detect backend intent marker on assistant metadata (no frontend intent detection). */
export function metadataIndicatesSportsNocIntent(md: Record<string, unknown> | undefined): boolean {
  if (!md) return false
  const intent = String(md.intent ?? '').trim()
  const kind = String(md.kind ?? '').trim()
  return intent === SPORTS_NOC_INTENT || kind === SPORTS_NOC_INTENT
}
